import React, { useEffect, useState } from 'react';
import Toast from '../../components/Common/Toast';
import AutomatizacionesSettings from '../../components/Admin/AutomatizacionesSettings';
import GeneralSettingsSection from '../../components/Admin/GeneralSettingsSection';
import SunatCredentialsForm from '../../components/Admin/SunatCredentialsForm';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { getModuleSettings, updateModuleSettings } from '../../services/moduleSettingsService';
import { saveSunatCredentials } from '../../services/sunatCredentialsService';

const GeneralSettings = () => {
  const {
    guidesRemoveStockRestrictions,
    setGuidesRemoveStockRestrictions,
    exitReferenceLastPurchaseCostEnabled,
    setExitReferenceLastPurchaseCostEnabled,
    productConversionsEnabled: contextProductConversionsEnabled,
    setProductConversionsEnabled,
    quickProductLineEnabled,
    setQuickProductLineEnabled,
    productsProjectEnabled,
    setProductsProjectEnabled,
    productsIgvEnabled,
    setProductsIgvEnabled,
    servicesClassificationEnabled,
    setServicesClassificationEnabled,
    loaded: moduleSettingsLoaded,
  } = useModuleSettings();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [productConversionsLocal, setProductConversionsLocal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProductConversions, setSavingProductConversions] = useState(false);
  const [savingGuidesRelax, setSavingGuidesRelax] = useState(false);
  const [savingExitReferenceCost, setSavingExitReferenceCost] = useState(false);
  const [savingQuickProductLine, setSavingQuickProductLine] = useState(false);
  const [savingProductsProject, setSavingProductsProject] = useState(false);
  const [savingProductsIgv, setSavingProductsIgv] = useState(false);
  const [savingServicesClassification, setSavingServicesClassification] = useState(false);
  const [kardexInconsistenciesFromEnabled, setKardexInconsistenciesFromEnabled] = useState(false);
  const [kardexInconsistenciesFromDate, setKardexInconsistenciesFromDate] = useState('');
  const [kardexInconsistenciesFromTime, setKardexInconsistenciesFromTime] = useState('00:00');
  const [savingKardexInconsistenciesFrom, setSavingKardexInconsistenciesFrom] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getModuleSettings()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setEnabled(Boolean(res.data.product_warehouse_visibility_enabled));
          setProductConversionsLocal(Boolean(res.data.product_conversions_enabled));
          setKardexInconsistenciesFromEnabled(Boolean(res.data.kardex_inconsistencies_count_from_enabled));
          setKardexInconsistenciesFromDate(res.data.kardex_inconsistencies_count_from_date || '');
          setKardexInconsistenciesFromTime(
            res.data.kardex_inconsistencies_count_from_time
              ? String(res.data.kardex_inconsistencies_count_from_time).slice(0, 5)
              : '00:00'
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToast({
            isOpen: true,
            message: 'No se pudo cargar la configuración general',
            type: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const onToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      const res = await updateModuleSettings({
        product_warehouse_visibility_enabled: next,
      });
      if (!res.success) throw new Error('No se pudo guardar');
      setToast({
        isOpen: true,
        message: 'Configuración general guardada',
        type: 'success',
      });
    } catch (_) {
      setEnabled(!next);
      setToast({
        isOpen: true,
        message: 'No se pudo guardar la configuración general',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const productConversionsEnabled = moduleSettingsLoaded
    ? contextProductConversionsEnabled
    : productConversionsLocal;

  const onToggleProductConversions = () => {
    const next = !productConversionsEnabled;
    setSavingProductConversions(true);
    setProductConversionsEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Configuración general guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la configuración general',
          type: 'error',
        });
      })
      .finally(() => {
        setSavingProductConversions(false);
      });
  };

  const onToggleQuickProductLine = () => {
    const next = !quickProductLineEnabled;
    setSavingQuickProductLine(true);
    setQuickProductLineEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Configuración general guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la configuración general',
          type: 'error',
        });
      })
      .finally(() => setSavingQuickProductLine(false));
  };

  const onToggleGuidesRelax = () => {
    const next = !guidesRemoveStockRestrictions;
    setSavingGuidesRelax(true);
    setGuidesRemoveStockRestrictions(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Configuración general guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la configuración general',
          type: 'error',
        });
      })
      .finally(() => setSavingGuidesRelax(false));
  };

  const onToggleExitReferenceCost = () => {
    const next = !exitReferenceLastPurchaseCostEnabled;
    setSavingExitReferenceCost(true);
    setExitReferenceLastPurchaseCostEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Configuración general guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la configuración general',
          type: 'error',
        });
      })
      .finally(() => setSavingExitReferenceCost(false));
  };

  const onToggleProductsProject = () => {
    const next = !productsProjectEnabled;
    setSavingProductsProject(true);
    setProductsProjectEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Configuración general guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la configuración general',
          type: 'error',
        });
      })
      .finally(() => setSavingProductsProject(false));
  };

  const onToggleProductsIgv = () => {
    const next = !productsIgvEnabled;
    setSavingProductsIgv(true);
    setProductsIgvEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Configuración general guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la configuración general',
          type: 'error',
        });
      })
      .finally(() => setSavingProductsIgv(false));
  };

  const onToggleServicesClassification = () => {
    const next = !servicesClassificationEnabled;
    setSavingServicesClassification(true);
    setServicesClassificationEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Configuración general guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la configuración general',
          type: 'error',
        });
      })
      .finally(() => setSavingServicesClassification(false));
  };

  const saveKardexInconsistenciesFrom = async (payload) => {
    setSavingKardexInconsistenciesFrom(true);
    try {
      const res = await updateModuleSettings(payload);
      if (!res.success) throw new Error('No se pudo guardar');
      if (res.data) {
        setKardexInconsistenciesFromEnabled(Boolean(res.data.kardex_inconsistencies_count_from_enabled));
        setKardexInconsistenciesFromDate(res.data.kardex_inconsistencies_count_from_date || '');
        setKardexInconsistenciesFromTime(
          res.data.kardex_inconsistencies_count_from_time
            ? String(res.data.kardex_inconsistencies_count_from_time).slice(0, 5)
            : '00:00'
        );
      }
      setToast({
        isOpen: true,
        message: 'Configuración general guardada',
        type: 'success',
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'No se pudo guardar la configuración general';
      setToast({ isOpen: true, message: msg, type: 'error' });
      throw err;
    } finally {
      setSavingKardexInconsistenciesFrom(false);
    }
  };

  const onToggleKardexInconsistenciesFrom = async () => {
    const next = !kardexInconsistenciesFromEnabled;
    const today = new Date().toISOString().slice(0, 10);
    const date = kardexInconsistenciesFromDate || today;
    const time = kardexInconsistenciesFromTime || '00:00';

    setKardexInconsistenciesFromEnabled(next);
    if (next) {
      setKardexInconsistenciesFromDate(date);
      setKardexInconsistenciesFromTime(time);
    }

    try {
      await saveKardexInconsistenciesFrom({
        kardex_inconsistencies_count_from_enabled: next,
        ...(next
          ? {
              kardex_inconsistencies_count_from_date: date,
              kardex_inconsistencies_count_from_time: time,
            }
          : {
              kardex_inconsistencies_count_from_date: null,
              kardex_inconsistencies_count_from_time: null,
            }),
      });
    } catch {
      setKardexInconsistenciesFromEnabled(!next);
    }
  };

  const onKardexInconsistenciesFromDateTimeBlur = async () => {
    if (!kardexInconsistenciesFromEnabled) return;
    if (!kardexInconsistenciesFromDate) {
      setToast({
        isOpen: true,
        message: 'Indique la fecha desde la cual contar inconsistencias.',
        type: 'warning',
      });
      return;
    }
    try {
      await saveKardexInconsistenciesFrom({
        kardex_inconsistencies_count_from_enabled: true,
        kardex_inconsistencies_count_from_date: kardexInconsistenciesFromDate,
        kardex_inconsistencies_count_from_time: kardexInconsistenciesFromTime || '00:00',
      });
    } catch {
      /* toast ya mostrado */
    }
  };

  const handleSunatCredentialsSave = async (credentials) => {
    if (!credentials.ruc || !credentials.usuario || !credentials.password) {
      setToast({
        isOpen: true,
        message: 'Completa todos los campos de SUNAT',
        type: 'error',
      });
      return { success: false };
    }

    try {
      const res = await saveSunatCredentials({
        ruc: credentials.ruc,
        usuario_sol: credentials.usuario,
        password_sol: credentials.password,
      });

      if (res.success) {
        setToast({
          isOpen: true,
          message: res.message || 'Credenciales SUNAT guardadas',
          type: 'success',
        });
        return { success: true };
      }

      setToast({
        isOpen: true,
        message: res.message || 'Error al guardar credenciales SUNAT',
        type: 'error',
      });
      return { success: false };
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setToast({
        isOpen: true,
        message: apiMessage || 'Error de conexión al guardar credenciales SUNAT',
        type: 'error',
      });
      return { success: false };
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Configuraciones generales</h1>
        <p className="mt-1 text-sm text-gray-500">
          Ajustes globales del sistema. Esta sección controla comportamientos transversales.
        </p>
      </div>

      <GeneralSettingsSection
        title="Sistema"
        description="Comportamientos globales que afectan varios módulos del sistema."
      >
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Selección de almacenes al crear producto</p>
            <p className="text-sm text-gray-500">
              Si está activo, al crear un producto se podrá elegir en qué almacén o almacenes estará visible.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">{enabled ? 'Activo' : 'Inactivo'}</span>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={onToggle}
              disabled={loading || saving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                enabled ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  enabled ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Conversiones por producto</p>
            <p className="text-sm text-gray-500">
              Si está activo, al crear o editar un producto se podrán definir tasas de conversión
              personalizadas entre unidades (por ejemplo, 1 KG = 12 UN).
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">
              {productConversionsEnabled ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={productConversionsEnabled}
              onClick={onToggleProductConversions}
              disabled={loading || savingProductConversions}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                productConversionsEnabled ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  productConversionsEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Línea rápida de productos</p>
            <p className="text-sm text-gray-500">
              Si está activo, en órdenes de compra, requerimientos, documentos y guías aparece una barra fija
              arriba de la tabla para capturar productos y agregarlos con un clic.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">
              {quickProductLineEnabled ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={quickProductLineEnabled}
              onClick={onToggleQuickProductLine}
              disabled={loading || savingQuickProductLine || !moduleSettingsLoaded}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                quickProductLineEnabled ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  quickProductLineEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Habilitar proyecto en productos</p>
            <p className="text-sm text-gray-500">
              Si está activo, aparece un selector de proyecto por defecto al agregar productos. Cada línea
              sigue mostrando el proyecto para poder cambiarlo si hace falta.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">
              {productsProjectEnabled ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={productsProjectEnabled}
              onClick={onToggleProductsProject}
              disabled={loading || savingProductsProject || !moduleSettingsLoaded}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                productsProjectEnabled ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  productsProjectEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Habilitar IGV en productos</p>
            <p className="text-sm text-gray-500">
              Si está activo, aparece un selector de IGV por defecto al agregar productos. Cada línea
              sigue permitiendo cambiar si lleva IGV o no, para documentos con ítems mixtos.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">
              {productsIgvEnabled ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={productsIgvEnabled}
              onClick={onToggleProductsIgv}
              disabled={loading || savingProductsIgv || !moduleSettingsLoaded}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                productsIgvEnabled ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  productsIgvEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Habilitar clasificación de servicios</p>
            <p className="text-sm text-gray-500">
              Si está activo, puede definir categorías de servicios y asignarlas al crear o editar cada servicio.
              Los servicios sin familia aparecerán como pendientes de clasificación.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">
              {servicesClassificationEnabled ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={servicesClassificationEnabled}
              onClick={onToggleServicesClassification}
              disabled={loading || savingServicesClassification || !moduleSettingsLoaded}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                servicesClassificationEnabled ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  servicesClassificationEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Quitar restricciones a guías</p>
            <p className="text-sm text-gray-500">
              En guías de salida y traspaso permite editar cantidad y costo unitario sin validar stock disponible
              ni bloquear campos por falta de inventario.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">
              {guidesRemoveStockRestrictions ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={guidesRemoveStockRestrictions}
              onClick={onToggleGuidesRelax}
              disabled={loading || savingGuidesRelax || !moduleSettingsLoaded}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                guidesRemoveStockRestrictions ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  guidesRemoveStockRestrictions ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Costo referencial a última compra global</p>
            <p className="text-sm text-gray-500">
              En guías de salida, traspaso y recepción muestra en el documento e imprimibles el costo de la última
              compra global del producto (o 0 si no hay). El kardex sigue usando su costo real interno.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-500">
              {exitReferenceLastPurchaseCostEnabled ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={exitReferenceLastPurchaseCostEnabled}
              onClick={onToggleExitReferenceCost}
              disabled={loading || savingExitReferenceCost || !moduleSettingsLoaded}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                exitReferenceLastPurchaseCostEnabled ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  exitReferenceLastPurchaseCostEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-gray-900">Contar inconsistencias de kardex desde</p>
              <p className="text-sm text-gray-500">
                Si está activo, el reporte de inconsistencias y los indicadores del sistema solo consideran
                movimientos en kardex a partir de la fecha y hora indicadas. Los movimientos anteriores se
                usan para calcular saldos, pero no se marcan como inconsistencia.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-sm text-gray-500">
                {kardexInconsistenciesFromEnabled ? 'Activo' : 'Inactivo'}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={kardexInconsistenciesFromEnabled}
                onClick={onToggleKardexInconsistenciesFrom}
                disabled={loading || savingKardexInconsistenciesFrom}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-60 ${
                  kardexInconsistenciesFromEnabled ? 'bg-teal-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    kardexInconsistenciesFromEnabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                  aria-hidden
                />
              </button>
            </div>
          </div>

          {kardexInconsistenciesFromEnabled ? (
            <div className="flex flex-wrap items-end gap-4 pt-2 border-t border-gray-100">
              <div>
                <label htmlFor="kardex-inconsistencies-from-date" className="block text-xs font-medium text-gray-600 mb-1">
                  Fecha desde
                </label>
                <input
                  id="kardex-inconsistencies-from-date"
                  type="date"
                  value={kardexInconsistenciesFromDate}
                  onChange={(e) => setKardexInconsistenciesFromDate(e.target.value)}
                  onBlur={onKardexInconsistenciesFromDateTimeBlur}
                  disabled={savingKardexInconsistenciesFrom}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="kardex-inconsistencies-from-time" className="block text-xs font-medium text-gray-600 mb-1">
                  Hora desde
                </label>
                <input
                  id="kardex-inconsistencies-from-time"
                  type="time"
                  value={kardexInconsistenciesFromTime}
                  onChange={(e) => setKardexInconsistenciesFromTime(e.target.value)}
                  onBlur={onKardexInconsistenciesFromDateTimeBlur}
                  disabled={savingKardexInconsistenciesFrom}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
      </GeneralSettingsSection>

      <GeneralSettingsSection
        title="Integraciones"
        description="Credenciales y conexiones con servicios externos."
      >
        <SunatCredentialsForm className="" onSave={handleSunatCredentialsSave} />
      </GeneralSettingsSection>

      <AutomatizacionesSettings />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default GeneralSettings;
